import React, {useCallback, useEffect, useState} from "react";
import AppBar from "@material-ui/core/AppBar";
import {
    ExpanderTable, ExpanderTableContent,
    ExpanderTableHeader,
    ExpanderTablePager, ExpanderTableRows,
    HeaderToolbar,
} from "../../components";
import {makeStyles} from "@material-ui/core/styles";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Menu from "@material-ui/core/Menu";
import {fetchAccount} from "../../store/actions";
import {useStore} from "../../store";
import {ErrorScreen} from "../../components";
import {Loading} from "../../components";
import {animateScroll} from 'react-scroll'
import ausPhone from "../../utils/aus-phone";
import format from "../../utils/format";
import {CallsService} from "../../services";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton"
import Phone from "@material-ui/icons/Phone"
import PhoneForwarded from "@material-ui/icons/PhoneForwarded"
import PhoneDisabled from "@material-ui/icons/PhoneDisabled"
import PhoneMissed from "@material-ui/icons/PhoneMissed"
import CallEnd from "@material-ui/icons/CallEnd"
import MoreVert from "@material-ui/icons/MoreVert";
import Refresh from "@material-ui/icons/Refresh";
import FuseAnimate from "../../components/fuse-animate/fuse-animate";

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
    },
    content: {
        display       : 'flex',
        flexDirection : 'column',
        flexGrow      : 1,
        marginTop    : theme.spacing(2),
    },
    dataLabel: {
        minWidth: 75,
        fontWeight: 500,
    },
}));


const CallLogPage = (props) => {
    const classes = useStyles();
    // const {user} = useAuthUser();        // Get logged in user
    const [store, dispatch] = useStore();   // Get the global store
    const [state, setState] = useState({
        isFetching : false,
        error      : null,
        calls      : [],    // Call data
        total      : 0,     // total number of calls
        filtered   : 0,     // total number of calls after filtering
        page       : 0,     // current page (numbered from 0)
        pageLength : 10,    // number of records to display per page
        pageCount  : 0,     // total number of pages available
        scrollTop  : false, // If set, scroll to the top of the page after fetching
        showLoader : false, // if true display the table loading indicator
    });
    const [menuAnchor, setMenuAnchor] = useState(null)
    const tableRef = React.createRef();

    // Scroll to top on mount
    useEffect( () => {
        document.getElementById('psb').scrollTo(0,0);
    }, [])  // <- empty deps means it runs only on load

    // Make sure the account is available in the store
    useEffect(()=> {
        if (store.user.accountId) {
            dispatch(fetchAccount(store.user.accountId, store, dispatch))
        }
    }, [store.user.accountId, store, dispatch])


    // fetchData is a callback that returns row data for the ExpanderTable
    //
    // When the table requires data it calls this function passing four parameters:
    //  - page:        the page number (first page is zero)
    //  - pageLength:  the maximum number of rows to return
    //  - sorting:     the expected sort columns and order of the result set.
    //  - search:      the user's search term if any
    //
    // The callback should return an object:
    // {
    //     rows:     array - the result rows (or an empty array)
    //     total:    int   - the total number of rows available
    //     filtered: int   - the total number of rows returned after applying the search term.
    // }
    //
    // The function should be memoized using useCallback otherwise a new function will be passed to the
    // table each render which will cause the table to request the data each time it renders.
    const fetchData = useCallback((page, pageLength, sorting={}) => {
        return new Promise((resolve, reject) => {

            // If we don't have an accountId yet, we can't proceed...
            if (store.account.id === 0) {
                resolve({total: 0, filtered: 0, rows: []})
                return
            }

            // Set the sort order
            const sort = sorting.column && sorting.direction ? sorting.column+','+sorting.direction : 'startTime,desc';

            CallsService.getForAccount(store.account.id, page * pageLength, pageLength, sort)
                .then(result => {
                    resolve({
                        total    : result.total,
                        filtered : result.filtered,
                        rows     : result.calls,
                    })
                })
                .catch(error => {
                    setState(s =>({...s, isFetching: false, error}))
                })

        })
    }, [store.account.id])


    const handlePageVisibility = (isVisible, viewportOffset) => {
        // If the page controls at the bottom of the table are offscreen and above
        // the viewport, scroll to the top
        if (!isVisible && viewportOffset < 0) {
            animateScroll.scrollToTop({containerId: 'psb'});
        }
    }

    const handleMenuButtonClick = (event) => {
        // Open the menu at the
        setMenuAnchor(event.currentTarget)
    }
    const handleMenuItemClick = (event) => {
        if (event.target.id === 'refresh' && tableRef.current) {
            tableRef.current.reload();
        }
        setMenuAnchor(null);
    }
    const handleMenuClose = (event) => {
        setMenuAnchor(null);
    }

    return(
        <div className={classes.root}>
            <AppBar position="fixed" className={classes.appBar}>
                <HeaderToolbar location="calls"/>
            </AppBar>
            <Toolbar />

            <FuseAnimate animation="transition.expandIn" delay={200}>
                <main className={classes.content}>
                    { store.account.error && <div className="my-auto pb-24"><ErrorScreen error={store.account.error}/></div> }
                    { state.error && <div className="my-auto pb-24"><ErrorScreen error={state.error}/></div> }
                    { store.account.isFetching ? <div className="my-auto pb-24"><Loading/></div> : null }
                    {store.account.error === null && state.error === null && !store.account.isFetching ?
                        <div className="flex flex-col flex-grow">
                            <Typography variant="h5" color="secondary">Call Log</Typography>

                            <ExpanderTable
                                onData={fetchData}
                                ref={tableRef}
                                columns={[
                                    {field: 'from',      title: 'From',     style: 'ml-8 mr-1 w-7/12 sm:w-1/4'},
                                    {field: 'to',        title: 'To',       style: 'hidden sm:flex sm:w-1/4'},
                                    {field: 'startTime', title: 'When',     style: 'w-5/12 sm:w-1/4'},
                                    {field: 'duration',  title: 'Duration', style: 'hidden sm:flex sm:w-1/4'}
                                ]}>

                                <ExpanderTableHeader className="pt-4 pb-2 pl-4 pr-1 uppercase" >
                                    <div style={{marginTop:-12, marginBottom: -10}}>
                                        <IconButton onClick={handleMenuButtonClick}>
                                            <MoreVert/>
                                        </IconButton>
                                        <Menu
                                            anchorEl={menuAnchor}
                                            keepMounted
                                            open={Boolean(menuAnchor)}
                                            onClose={handleMenuClose}
                                        >
                                            <MenuItem
                                                id="refresh"
                                                onClick={handleMenuItemClick}
                                            >
                                                <Refresh className="mr-2"/>
                                                Refresh
                                            </MenuItem>
                                        </Menu>
                                    </div>
                                </ExpanderTableHeader>

                                <ExpanderTableContent noData="No calls to show.">
                                    <ExpanderTableRows
                                        rowKey="sid"
                                        SummaryComponent={CallSummary}
                                        ContentComponent={CallContent}
                                    />
                                </ExpanderTableContent>

                                <ExpanderTablePager onVisibilityChange={handlePageVisibility}/>
                            </ExpanderTable>
                        </div>
                        : null
                    }
                </main>
            </FuseAnimate>
        </div>
    )

}

// CallSummary renders the header for a row in the ExpanderTable
const CallSummary = ({row}) => {
    return (
        <div className="flex flex-row flex-grow font-light">
            <div className="mr-3"><CallStatusIcon status={row.status} fontSize="small" color="textSecondary" /></div>
            <div className="mr-1 w-7/12 sm:w-1/4 font-bold">{ausPhone(row.from)}</div>
            <div className="hidden sm:flex sm:w-1/4">{ausPhone(row.to)}</div>
            <div className="w-5/12 sm:w-1/4">{format('ago',row.startTime)}</div>
            <div className="hidden sm:flex sm:w-1/4">{format('duration', row.duration)}</div>
        </div>
    )
}

// CallContent renders the content in the expander in the ExpanderTable
const CallContent = ({row}) => {
    const classes = {
        dataLabel: {
            minWidth: 75,
            fontWeight: 500,
        },
    };

    return (
        <div className="flex flex-col flex-grow font-light">

            <div className="flex flex-col md:flex-row">
                <div className="flex flex-row p-1 md:w-1/2">
                    <div style={classes.dataLabel}>Time</div>
                    <div className="">
                        <span className="pr-1">{format('date-long', row.startTime)}</span>
                        <span className="" dangerouslySetInnerHTML={{__html: format('time-12h', row.startTime)}}/>
                    </div>
                </div>
                <div className="flex flex-row p-1 md:w-1/2">
                    <div style={classes.dataLabel}>Direction</div>
                    <div className="capitalize">{format('direction', row.direction)}</div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row">
                <div className="flex flex-row p-1 md:w-1/2">
                    <div style={classes.dataLabel}>From</div>
                    <div className="">{ausPhone(row.from)}</div>
                </div>
                <div className="flex flex-row p-1 md:w-1/2">
                    <div style={classes.dataLabel}>To</div>
                    <div className="">{ausPhone(row.to)}</div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row">
                <div className="flex flex-row p-1 md:w-1/2">
                    <div style={classes.dataLabel}>Duration</div>
                    <div className="">{format('duration', row.duration)}</div>
                </div>
                <div className="flex flex-row p-1 md:w-1/2">
                    <div style={classes.dataLabel}>Status</div>
                    <div className="capitalize">{row.status}</div>
                </div>
            </div>

        </div>
    )
}

const CallStatusIcon = ({status, fontSize}) => {
    switch (status) {
        case 'completed':
            return <PhoneForwarded fontSize={fontSize} color="inherit" className="text-green-600" />
        case 'cancel':
            return <CallEnd fontSize={fontSize} color="inherit" className="text-blue-600" />
        case 'no-answer':
        case 'noanswer':
            return <PhoneMissed fontSize={fontSize} color="inherit" className="text-orange-600" />
        case 'failed':
            return <PhoneDisabled fontSize={fontSize} color="inherit" className="text-red-600"/>
        default:
            return <Phone fontSize={fontSize}/>
    }
}

export default CallLogPage;